import { useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, Download, Loader2, MessageSquare, Share2, Send } from "lucide-react";
import { AnalysisData } from "@/pages/Index";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import ReactMarkdown from "react-markdown";
import jsPDF from "jspdf";
import { supabase } from "@/integrations/supabase/client";

interface AnalysisResultsProps {
  data: AnalysisData | null;
  isAnalyzing: boolean;
  imageUrl?: string;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export const AnalysisResults = ({ data, isAnalyzing, imageUrl }: AnalysisResultsProps) => {
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const downloadJSON = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'analysis-results.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success("JSON downloaded!");
  };

  const downloadCSV = () => {
    if (!data) return;
    
    // Create CSV content
    let csv = 'Category,Value,Confidence\n';
    
    // Add tags
    data.tags.forEach(tag => {
      csv += `Tag,"${tag.label}",${Math.round(tag.confidence * 100)}%\n`;
    });
    
    // Add colors
    data.colors.forEach((color, i) => {
      csv += `Color ${i + 1},"${color}",N/A\n`;
    });
    
    // Add quality if available
    if (data.quality) {
      csv += `Quality Score,"${data.quality.score}/10",N/A\n`;
      csv += `Brightness,"${data.quality.brightness}",N/A\n`;
      csv += `Sharpness,"${data.quality.sharpness}",N/A\n`;
      csv += `Composition,"${data.quality.composition}",N/A\n`;
    }
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'analysis-results.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded!");
  };

  const downloadPDF = async () => {
    if (!data) return;

    try {
      const pdf = new jsPDF();
      let yPosition = 20;

      // Title
      pdf.setFontSize(20);
      pdf.text("Image Analysis Report", 20, yPosition);
      yPosition += 15;

      // Metadata
      pdf.setFontSize(10);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, yPosition);
      yPosition += 10;

      // Add thumbnail if available
      if (imageUrl) {
        try {
          pdf.addImage(imageUrl, "JPEG", 20, yPosition, 80, 60);
          yPosition += 70;
        } catch (e) {
          console.log("Could not add image to PDF");
        }
      }

      // Helper function to add sections
      const addSection = (title: string, content: string, maxWidth = 170) => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.setFontSize(12);
        pdf.setFont(undefined, "bold");
        pdf.text(title, 20, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, "normal");
        const lines = pdf.splitTextToSize(content, maxWidth);
        pdf.text(lines, 20, yPosition);
        yPosition += lines.length * 5 + 5;
      };

      // Add content
      if (data.caption) addSection("Caption", data.caption);
      if (data.scene) addSection("Scene Description", data.scene);
      if (data.altText) addSection("SEO Alt Text", data.altText);
      if (data.socialCaption) addSection("Social Media Caption", data.socialCaption);
      
      if (data.tags && data.tags.length > 0) {
        addSection("Tags", data.tags.map(t => `${t.label} (${(t.confidence * 100).toFixed(0)}%)`).join(", "));
      }
      
      if (data.colors && data.colors.length > 0) {
        addSection("Dominant Colors", data.colors.join(", "));
      }
      
      if (data.ocrText) addSection("Extracted Text (OCR)", data.ocrText);
      
      if (data.quality) {
        addSection("Quality Analysis", 
          `Score: ${data.quality.score}/100\n` +
          `Brightness: ${data.quality.brightness}\n` +
          `Sharpness: ${data.quality.sharpness}\n` +
          `Composition: ${data.quality.composition}`
        );
      }

      if (data.exif) {
        const exifText = [
          data.exif.make && `Camera: ${data.exif.make} ${data.exif.model || ''}`,
          data.exif.dateTime && `Date: ${data.exif.dateTime}`,
          data.exif.focalLength && `Focal Length: ${data.exif.focalLength}`,
          data.exif.iso && `ISO: ${data.exif.iso}`,
          data.exif.exposureTime && `Exposure: ${data.exif.exposureTime}`,
          data.exif.fNumber && `Aperture: ${data.exif.fNumber}`,
        ].filter(Boolean).join("\n");
        
        if (exifText) addSection("Camera EXIF Data", exifText);
      }

      pdf.save(`analysis-report-${Date.now()}.pdf`);
      toast.success("PDF report downloaded");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const downloadTXT = () => {
    if (!data) return;
    
    let txt = `AI PHOTO ANALYSIS RESULTS\n`;
    txt += `${'='.repeat(50)}\n\n`;
    
    txt += `CAPTION:\n${data.caption}\n\n`;
    
    txt += `SCENE DESCRIPTION:\n${data.scene}\n\n`;
    
    txt += `OBJECT TAGS:\n`;
    data.tags.forEach(tag => {
      txt += `- ${tag.label} (${Math.round(tag.confidence * 100)}%)\n`;
    });
    txt += `\n`;
    
    txt += `COLOR PALETTE:\n`;
    data.colors.forEach((color, i) => {
      txt += `- Color ${i + 1}: ${color}\n`;
    });
    txt += `\n`;
    
    if (data.ocrText) {
      txt += `EXTRACTED TEXT (OCR):\n${data.ocrText}\n\n`;
    }
    
    if (data.quality) {
      txt += `QUALITY ASSESSMENT:\n`;
      txt += `- Overall Score: ${data.quality.score}/10\n`;
      txt += `- Brightness: ${data.quality.brightness}\n`;
      txt += `- Sharpness: ${data.quality.sharpness}\n`;
      txt += `- Composition: ${data.quality.composition}\n\n`;
    }
    
    txt += `SEO ALT TEXT:\n${data.altText}\n\n`;
    txt += `SOCIAL CAPTION:\n${data.socialCaption}\n\n`;
    
    if (data.exif) {
      txt += `CAMERA METADATA (EXIF):\n`;
      if (data.exif.make) txt += `- Make: ${data.exif.make}\n`;
      if (data.exif.model) txt += `- Model: ${data.exif.model}\n`;
      if (data.exif.dateTime) txt += `- Date: ${data.exif.dateTime}\n`;
      if (data.exif.focalLength) txt += `- Focal Length: ${data.exif.focalLength}\n`;
      if (data.exif.iso) txt += `- ISO: ${data.exif.iso}\n`;
      if (data.exif.exposureTime) txt += `- Exposure: ${data.exif.exposureTime}\n`;
      if (data.exif.fNumber) txt += `- Aperture: ${data.exif.fNumber}\n`;
    }
    
    const blob = new Blob([txt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'analysis-results.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success("TXT report downloaded!");
  };

  if (!data && !isAnalyzing) {
    return (
      <Card className="p-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            Upload an image to see analysis results
          </p>
        </div>
      </Card>
    );
  }

  if (isAnalyzing) {
    return (
      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <h2 className="text-xl font-semibold">Analyzing...</h2>
        </div>
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Export Options */}
      <Card className="p-4">
        <div className="space-y-3">
          <h3 className="font-semibold text-foreground">Export Results</h3>
          <div className="flex flex-wrap gap-2">
            <Button onClick={downloadJSON} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              JSON
            </Button>
            <Button onClick={downloadCSV} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              CSV
            </Button>
            <Button onClick={downloadTXT} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              TXT Report
            </Button>
          </div>
        </div>
      </Card>

      {/* Caption */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">Caption</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => copyToClipboard(data.caption, "Caption")}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-foreground">{data.caption}</p>
      </Card>

      {/* Tags */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">Object Tags</h3>
        </div>
        <div className="space-y-2">
          {data.tags.map((tag, index) => (
            <div key={index} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground">{tag.label}</span>
                <span className="text-muted-foreground">{Math.round(tag.confidence * 100)}%</span>
              </div>
              <Progress value={tag.confidence * 100} className="h-2" />
            </div>
          ))}
        </div>
      </Card>

      {/* Colors */}
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold text-foreground">Color Palette</h3>
        <div className="flex gap-2 flex-wrap">
          {data.colors.map((color, index) => (
            <div key={index} className="flex flex-col items-center gap-1">
              <div
                className="w-12 h-12 rounded-lg border border-border shadow-sm"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-muted-foreground font-mono">{color}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Scene Description */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">Scene Description</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => copyToClipboard(data.scene, "Scene description")}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-foreground leading-relaxed">{data.scene}</p>
      </Card>

      {/* SEO Alt Text */}
      <Card className="p-4 space-y-3 border-success/20 bg-success/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground">SEO Alt Text</h3>
            <Badge variant="outline" className="bg-success text-success-foreground">Optimized</Badge>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => copyToClipboard(data.altText, "Alt text")}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-foreground">{data.altText}</p>
      </Card>

      {/* Social Caption */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">Social Media Caption</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => copyToClipboard(data.socialCaption, "Social caption")}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-foreground">{data.socialCaption}</p>
      </Card>

      {/* OCR Text */}
      {data.ocrText && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Extracted Text (OCR)</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => copyToClipboard(data.ocrText!, "OCR text")}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-foreground whitespace-pre-wrap font-mono bg-muted p-3 rounded">
            {data.ocrText}
          </p>
        </Card>
      )}

      {/* EXIF Data */}
      {data.exif && (
        <Card className="p-4 space-y-3">
          <h3 className="font-semibold text-foreground">Camera Metadata (EXIF)</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {data.exif.make && (
              <div>
                <span className="text-muted-foreground">Camera:</span>
                <p className="text-foreground font-medium">{data.exif.make} {data.exif.model}</p>
              </div>
            )}
            {data.exif.dateTime && (
              <div>
                <span className="text-muted-foreground">Date:</span>
                <p className="text-foreground font-medium">{data.exif.dateTime}</p>
              </div>
            )}
            {data.exif.focalLength && (
              <div>
                <span className="text-muted-foreground">Focal Length:</span>
                <p className="text-foreground font-medium">{data.exif.focalLength}</p>
              </div>
            )}
            {data.exif.iso && (
              <div>
                <span className="text-muted-foreground">ISO:</span>
                <p className="text-foreground font-medium">{data.exif.iso}</p>
              </div>
            )}
            {data.exif.exposureTime && (
              <div>
                <span className="text-muted-foreground">Exposure:</span>
                <p className="text-foreground font-medium">{data.exif.exposureTime}s</p>
              </div>
            )}
            {data.exif.fNumber && (
              <div>
                <span className="text-muted-foreground">Aperture:</span>
                <p className="text-foreground font-medium">f/{data.exif.fNumber}</p>
              </div>
            )}
          </div>
          {data.exif.gps && (
            <div className="pt-2 border-t border-border">
              <span className="text-xs text-muted-foreground">
                GPS coordinates have been detected (stripped for privacy)
              </span>
            </div>
          )}
        </Card>
      )}

      {/* Quality Score */}
      {data.quality && (
        <Card className="p-4 space-y-3">
          <h3 className="font-semibold text-foreground">Image Quality Analysis</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Overall Score</span>
              <span className="text-lg font-bold text-foreground">{data.quality.score}/10</span>
            </div>
            <Progress value={data.quality.score * 10} className="h-2" />
            <div className="grid grid-cols-3 gap-2 pt-2 text-xs">
              <div className="text-center">
                <p className="text-muted-foreground">Brightness</p>
                <p className="font-medium text-foreground">{data.quality.brightness}</p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground">Sharpness</p>
                <p className="font-medium text-foreground">{data.quality.sharpness}</p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground">Composition</p>
                <p className="font-medium text-foreground">{data.quality.composition}</p>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
